// NavSidebar.tsx
import React, { useState, useEffect } from 'react';
import { useApiData } from '../../contexts/apiService';
import { PendingApprovalItem } from '../../contexts/tableDefinitions';
import { useUserAccess, USER_ROLE_LEVEL } from '../../contexts/userAccess';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import './sidebarstyle.css';
import { doSignOut } from '../../firebase/auth'
import { faSignOutAlt, faSun, faMoon, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDarkMode } from '../../contexts/darkModeContext';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import SidebarNavItem from './SidebarNavItem';

const NavSidebar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [hoverLogout, setHoverLogout] = useState(false);
    const [hoverDarkMode, setHoverDarkMode] = useState(false);
    const [hasAdminAccess, setHasAdminAccess] = useState(false);
    const [hasAccAccess, setHasAccAccess] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const navigate = useNavigate();
    const { isDarkMode, toggleDarkMode } = useDarkMode();
    const { apiData, userDetails, socketStatus, apiHealthStatus, heartbeatStats, triggerManualHeartbeat } = useApiData();
    const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
    const { userRole = 'viewer' } = useUserAccess();

    // Combined connection status: both socket and API health must be true
    const isFullyConnected = socketStatus && apiHealthStatus;

    useEffect(() => {
        if (userDetails && (userDetails.role === 'schedule_admin' || userDetails.role === 'admin')) {
            if (apiData?.pending_approvals) {
                const pendingApprovals = apiData.pending_approvals.filter(
                    (approval: PendingApprovalItem) => approval.status === 'Pending'
                );
                setPendingApprovalsCount(pendingApprovals.length)
            }
        }
        else if (userDetails && (userDetails.role !== 'schedule_admin' && userDetails.role !== 'admin')) {
            setPendingApprovalsCount(0)
        }

	}, [apiData?.pending_approvals, userDetails]);

    useEffect(() => {
        const auth = getAuth();
        const db = getFirestore();
        
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setUserEmail(user.email || '');
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setHasAccAccess(userData.role === 'accounting' || userData.canAccessTriggers === true);
                    setHasAdminAccess(userData.role === 'admin' || userData.canAccessTriggers === true);
                }
            } else {
                setHasAdminAccess(false);
                setHasAccAccess(false);
                setUserEmail('');
            }
        });
        
        return () => unsubscribe();
    }, []);

    const navigationItems = [
        { path: '/home', label: 'Dashboard', icon: '🏠', permissionLevel: 1, },
        { path: '/ticketdata', label: 'Ticket Data', icon: '📋', permissionLevel: 2, },
        { path: '/schedule', label: 'Schedule Table', icon: '📅', permissionLevel: 1, },
        // { path: '/mtdsummary', label: 'MTD Summary', icon: '💰', permissionLevel: 3, },
        { path: '/approvals', label: 'Approvals', icon: '✅', permissionLevel: 1, },
        { path: '/pdfapprovals', label: 'PDF Approvals', icon: '📄', permissionLevel: 2, },
        { path: '/maps', label: 'Map', icon: '🗺️', permissionLevel: 1, },
        { path: '/opstables', label: 'Operations Tables', icon: '⚙️', permissionLevel: 1, },
        { path: '/acctables', label: 'Accounting Tables', icon: '💵', permissionLevel: 3, },
        { path: '/triggers', label: 'Triggers', icon: '⚡', permissionLevel: 6, },
        { path: '/changelog', label: 'Changelog', icon: '📝', permissionLevel: 4, },
        { path: '/investigations', label: 'Investigations', icon: '🔍', permissionLevel: 4, },
        { path: '/admin', label: 'Admin', icon: '🔧', permissionLevel: 6, }
        
    ];

    return (
        <>
            <div className="left-banner">
                <div style={{ position: 'relative' }}>
                    <IconButton
                        style={{color: '#faf9f8'}}
                        aria-label="menu"
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        <MenuIcon/>
                    </IconButton>
                    
                    {pendingApprovalsCount > 0 && (
                        <div style={{
                            position: 'absolute',
                            top: '0px',
                            right: '0px',
                            backgroundColor: '#ff3333',
                            color: 'white',
                            borderRadius: '50%',
                            width: '16px',
                            height: '16px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                        }}>
                            {pendingApprovalsCount}
                        </div>
                    )}
                    
                </div>

                <div className="banner-controls">
                    <div className="sidebar-connection-status">
                        <span
                            className={`sidebar-connection-icon${isFullyConnected ? ' connected' : ''}`}
                            onClick={triggerManualHeartbeat}
                            style={{ cursor: 'pointer' }}
                        >
                            
                        </span>
                        <span className="sidebar-connection-tooltip">
                            {isFullyConnected ? (
                                <>
                                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>✅ Connected</div>
                                    <div style={{ fontSize: '0.9em' }}>
                                        Avg Latency: {heartbeatStats.avgLatency}ms<br/>
                                        Max Latency: {heartbeatStats.maxLatency}ms<br/>
                                        <span style={{ opacity: 0.7, fontSize: '0.85em' }}>
                                            ({heartbeatStats.count} samples, 10min)
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '0.8em', marginTop: '4px', opacity: 0.6, fontStyle: 'italic' }}>
                                        Click to test connection
                                    </div>
                                </>
                            ) : (
                                !apiHealthStatus && !socketStatus
                                    ? 'API & Socket Disconnected'
                                    : !apiHealthStatus
                                        ? 'API Disconnected'
                                        : 'Socket Disconnected'
                            )}
                        </span>
                    </div>
                    <div 
                        style={{ 
                            cursor: "pointer", 
                            backgroundColor: hoverDarkMode ? "#02667c" : "",
                            borderRadius: "50px",
                            padding: "5px",
                            marginRight: "10px"
                        }} 
                        onClick={toggleDarkMode}
                        onMouseEnter={() => setHoverDarkMode(true)}
                        onMouseLeave={() => setHoverDarkMode(false)}
                    >   
                        <span>
                            <FontAwesomeIcon 
                                icon={isDarkMode ? faSun : faMoon} 
                                style={{color: 'white'}}
                            />
                        </span>
                    </div>
                    <div 
                        style={{ 
                            cursor: "pointer", 
                            backgroundColor: hoverLogout ? "#02667c" : "",
                            borderRadius: "50px",
                            padding: "5px",
                        }} 
                        onClick={() => doSignOut().then(() => { navigate('/login') })}
                        onMouseEnter={() => setHoverLogout(true)}
                        onMouseLeave={() => setHoverLogout(false)}
                    >
                        <span>
                            <FontAwesomeIcon 
                                icon={faSignOutAlt} 
                                style={{color: 'white'}}
                            />
                        </span>
                    </div>
                </div>
            </div>
            <div className={`left-menu ${isOpen ? 'open' : ''}`}>
                <nav>
                    <ul className="nav-list">
                        {navigationItems.map((item) => (
                            <li key={item.path} className="nav-item">
                                {
                                    USER_ROLE_LEVEL[userRole] >= item.permissionLevel && (
                                        <SidebarNavItem
                                            navigateTo={item.path}
                                            label={item.label}
                                            icon={item.icon}
                                            onNavigate={() => setIsOpen(false)}
                                        />
                                    )
                                }

                                {item.label === 'Approvals' && pendingApprovalsCount > 0 && (
                                    <span style={{
                                        display: 'inline-flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        alignSelf: 'center',
                                        backgroundColor: '#ff3333',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: '16px',
                                        height: '16px',
                                        fontSize: '10px',
                                        marginLeft: '8px',
                                        marginRight: '8px',
                                        fontWeight: 'bold'
                                    }}>
                                        {pendingApprovalsCount}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </nav>
                {isOpen && userEmail && (
                    <div className="user-info" style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: '1rem',
                        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: 'white',
                        background: '#02667c',
                        fontSize: '0.875rem'
                    }}>
                        <FontAwesomeIcon icon={faUser} />
                        <span style={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {userEmail}
                        </span>
                    </div>
                )}
                
            </div>
            
            {isOpen && <div className="menu-overlay" onClick={() => setIsOpen(false)}></div>}
        </>
    );
};

export default NavSidebar;